import * as XLSX from 'xlsx';

const FOOTER_RE =
  /^load temp|pour|melt|voy range|disch range|heating plan period|boiler consp/i;
const LAST_N_DAYS = 7;

/** Matches 1-PORT, 1 - PORT, 1-Port, SLOP-PORT, etc. */
const TANK_CELL_RE =
  /(SLOP\s*[-–]?\s*(?:PORT|STBD)|\d+\s*[-–]\s*(?:PORT|STBD))/i;

export interface HeatPlanExcelFooter {
  loadTempByTank: Record<string, number>;
  voyMinByTank: Record<string, number>;
  voyMaxByTank: Record<string, number>;
}

export interface HeatPlanExcelTable {
  columns: string[];
  index: number[];
  data: unknown[][];
  footer?: HeatPlanExcelFooter;
}

function cellStr(row: unknown[] | undefined, col: number): string {
  if (!row || col < 0) {
    return '';
  }
  return String(row[col] ?? '').trim();
}

function toNumber(value: unknown): number | null {
  const s = String(value ?? '').trim();
  if (!s || s === '-' || /^x$/i.test(s) || /^on$/i.test(s)) {
    return null;
  }
  const n = Number(s.replace(/,/g, ''));
  return Number.isFinite(n) ? n : null;
}

function isHeatingTemp(n: number): boolean {
  return n >= 25 && n <= 120;
}

function normalizeTankKey(raw: string): string | null {
  const compact = raw.replace(/\s+/g, ' ').trim().toUpperCase();
  const m = compact.match(TANK_CELL_RE);
  if (!m) {
    return null;
  }
  const token = m[1].replace(/\s+/g, '').toUpperCase();
  if (token.startsWith('SLOP')) {
    return token.includes('STBD') ? 'SLOP-STBD' : 'SLOP-PORT';
  }
  const parts = token.match(/^(\d+)-(PORT|STBD)$/);
  return parts ? `${parts[1]}-${parts[2]}` : null;
}

function parseVoyRangeCell(value: unknown): { min: number; max: number } | null {
  const s = String(value ?? '').trim();
  const m = s.match(/(\d+(?:\.\d+)?)\s*[-–]\s*(\d+(?:\.\d+)?)/);
  if (!m) {
    return null;
  }
  const min = Number(m[1]);
  const max = Number(m[2]);
  if (!Number.isFinite(min) || !Number.isFinite(max)) {
    return null;
  }
  return { min, max };
}

function parseDateCell(value: unknown): number | null {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  if (typeof value === 'number' && value > 30000 && value < 70000) {
    const d = new Date((value - 25569) * 86400000);
    if (!Number.isNaN(d.getTime())) {
      return d.getTime();
    }
  }
  const s = String(value).trim();
  const m = s.match(/(\d{1,2})[\/\-\.](\d{1,2})[\/\-\.](\d{4})/);
  if (m) {
    return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  }
  const ts = Date.parse(s);
  return Number.isFinite(ts) ? ts : null;
}

function maxColumnCount(rows: unknown[][], rowIndices: number[]): number {
  return Math.max(
    0,
    ...rowIndices.map((ri) => (rows[ri] as unknown[] | undefined)?.length ?? 0),
  );
}

function findDateRowIndex(rows: unknown[][]): number {
  for (let i = 0; i < Math.min(rows.length, 100); i++) {
    const row = rows[i] as unknown[];
    if (!row) {
      continue;
    }
    for (let c = 0; c < row.length; c++) {
      const v = String(row[c] ?? '').trim();
      if (/^date$/i.test(v) || /^date\b/i.test(v)) {
        return i;
      }
    }
  }
  return -1;
}

function findTankOnColumn(
  rows: unknown[][],
  headerRows: number[],
  col: number,
): string | null {
  for (const ri of headerRows) {
    for (const offset of [0, -1, 1, -2, 2]) {
      const text = cellStr(rows[ri] as unknown[], col + offset);
      if (!text) {
        continue;
      }
      const key = normalizeTankKey(text);
      if (key) {
        return key;
      }
    }
  }
  return null;
}

function subLabelAt(
  rows: unknown[][],
  headerRows: number[],
  col: number,
): string {
  for (const ri of headerRows) {
    const v = cellStr(rows[ri] as unknown[], col).toLowerCase();
    if (v === 'temp' || v === 'heat') {
      return v;
    }
  }
  return '';
}

function resolveTempColumnIndex(
  rows: unknown[][],
  headerRows: number[],
  col: number,
): number {
  const at = subLabelAt(rows, headerRows, col);
  const next = subLabelAt(rows, headerRows, col + 1);
  const prev = subLabelAt(rows, headerRows, col - 1);

  if (at === 'temp') {
    return col;
  }
  if (next === 'temp') {
    return col + 1;
  }
  if (at === 'heat' && prev === 'temp') {
    return col - 1;
  }
  if (!at && next === 'heat') {
    return col;
  }
  return col;
}

/** Infer Temp columns from daily numeric data when headers are merged/unreadable. */
function inferPortTempColumnsFromData(
  rows: unknown[][],
  dateRowIdx: number,
  dateCol: number,
): Map<string, number> {
  const tankCols = new Map<string, number>();
  const maxCol = maxColumnCount(
    rows,
    Array.from({ length: Math.min(rows.length, dateRowIdx + 25) }, (_, i) => i),
  );

  const heatingCols: number[] = [];

  for (let c = dateCol + 1; c < maxCol; c++) {
    const headerBits: string[] = [];
    for (let ri = Math.max(0, dateRowIdx - 4); ri <= dateRowIdx + 3; ri++) {
      headerBits.push(cellStr(rows[ri] as unknown[], c).toLowerCase());
    }
    const headerText = headerBits.join(' ');
    if (
      /weighted|air temp|sea temp|wind|boiler|latitude|longitude|circulation|total load/i.test(
        headerText,
      )
    ) {
      continue;
    }

    let hits = 0;
    let rowsChecked = 0;
    for (let r = dateRowIdx + 1; r < Math.min(rows.length, dateRowIdx + 25); r++) {
      const row = rows[r] as unknown[];
      const label = cellStr(row, dateCol).toLowerCase();
      if (FOOTER_RE.test(label)) {
        break;
      }
      if (!parseDateCell(row?.[dateCol])) {
        continue;
      }
      rowsChecked++;
      const n = toNumber(row?.[c]);
      if (n !== null && isHeatingTemp(n)) {
        hits++;
      }
    }

    if (rowsChecked >= 2 && hits >= Math.max(2, Math.ceil(rowsChecked * 0.4))) {
      heatingCols.push(c);
    }
  }

  if (!heatingCols.length) {
    return tankCols;
  }

  const portTempCols: number[] = [];
  let i = 0;
  while (i < heatingCols.length) {
    const c = heatingCols[i];
    const sub = subLabelAt(
      rows,
      Array.from({ length: 8 }, (_, k) => Math.max(0, dateRowIdx - 3 + k)),
      c,
    );
    const subNext =
      i + 1 < heatingCols.length && heatingCols[i + 1] === c + 1
        ? subLabelAt(
            rows,
            Array.from({ length: 8 }, (_, k) => Math.max(0, dateRowIdx - 3 + k)),
            c + 1,
          )
        : '';

    if (sub === 'heat') {
      i++;
      continue;
    }
    portTempCols.push(c);
    if (subNext === 'heat' || (i + 1 < heatingCols.length && heatingCols[i + 1] === c + 1)) {
      i += 2;
    } else {
      i += 1;
    }
  }

  portTempCols.forEach((col, idx) => {
    const fromHeader = findTankOnColumn(
      rows,
      Array.from({ length: 10 }, (_, k) => Math.max(0, dateRowIdx - 5 + k)),
      col,
    );
    const key =
      fromHeader && fromHeader.endsWith('PORT')
        ? fromHeader
        : `${idx + 1}-PORT`;
    if (!tankCols.has(key)) {
      tankCols.set(key, col);
    }
  });

  return tankCols;
}

function mapTempColumns(
  rows: unknown[][],
  dateRowIdx: number,
): { dateCol: number; tankCols: Map<string, number>; weightedCol: number | null } {
  const headerRows: number[] = [];
  for (let i = Math.max(0, dateRowIdx - 6); i <= Math.min(rows.length - 1, dateRowIdx + 4); i++) {
    headerRows.push(i);
  }

  const maxCol = maxColumnCount(rows, headerRows);
  let dateCol = 0;
  let weightedCol: number | null = null;
  const tankCols = new Map<string, number>();

  for (let c = 0; c < maxCol; c++) {
    for (const ri of headerRows) {
      if (/^date$/i.test(cellStr(rows[ri] as unknown[], c))) {
        dateCol = c;
      }
    }
  }

  for (let c = 0; c < maxCol; c++) {
    const headerText = headerRows
      .map((ri) => cellStr(rows[ri] as unknown[], c))
      .join(' ')
      .toLowerCase();
    if (/weighted\s*avg/i.test(headerText)) {
      weightedCol = c;
    }
  }

  for (let c = 0; c < maxCol; c++) {
    const tankKey = findTankOnColumn(rows, headerRows, c);
    if (!tankKey || !tankKey.endsWith('PORT')) {
      continue;
    }

    const tempCol = resolveTempColumnIndex(rows, headerRows, c);
    const sub = subLabelAt(rows, headerRows, tempCol);
    if (sub === 'heat') {
      continue;
    }

    const existing = tankCols.get(tankKey);
    if (existing === undefined || sub === 'temp') {
      tankCols.set(tankKey, tempCol);
    }
  }

  if (!tankCols.size) {
    const inferred = inferPortTempColumnsFromData(rows, dateRowIdx, dateCol);
    inferred.forEach((col, tank) => tankCols.set(tank, col));
  }

  if (!weightedCol) {
    for (let c = 0; c < maxCol; c++) {
      const headerText = headerRows
        .map((ri) => cellStr(rows[ri] as unknown[], c))
        .join(' ')
        .toLowerCase();
      if (/weighted/.test(headerText)) {
        weightedCol = c;
        break;
      }
    }
  }

  return { dateCol, tankCols, weightedCol };
}

function orderedPortTanks(tankCols: Map<string, number>): string[] {
  return [...tankCols.entries()]
    .filter(([t]) => t.endsWith('PORT') && !t.startsWith('SLOP'))
    .sort((a, b) => parseInt(a[0], 10) - parseInt(b[0], 10))
    .map(([t]) => t);
}

function parseSheetRows(rows: unknown[][]): HeatPlanExcelTable {
  const dateRowIdx = findDateRowIndex(rows);
  if (dateRowIdx < 0) {
    throw new Error('DATE row not found');
  }

  const { dateCol, tankCols, weightedCol } = mapTempColumns(rows, dateRowIdx);
  let orderedTanks = orderedPortTanks(tankCols);

  if (!orderedTanks.length) {
    orderedTanks = [...tankCols.keys()]
      .filter((t) => t.includes('PORT'))
      .sort((a, b) => parseInt(a, 10) - parseInt(b, 10));
  }

  if (!orderedTanks.length) {
    throw new Error(
      'Could not find tank columns. Ensure the file is the Cargo Heating Plan Excel with DATE and 1-PORT, 2-PORT, … Temp columns.',
    );
  }

  const columns: string[] = orderedTanks.map((t) => `${t} (°C)`);
  if (weightedCol !== null) {
    columns.push('Weighted Avg Temp');
  }

  const colIndexes = orderedTanks.map((t) => tankCols.get(t)!);
  if (weightedCol !== null) {
    colIndexes.push(weightedCol);
  }

  const index: number[] = [];
  const data: unknown[][] = [];
  const footer: HeatPlanExcelFooter = {
    loadTempByTank: {},
    voyMinByTank: {},
    voyMaxByTank: {},
  };

  for (let r = dateRowIdx + 1; r < rows.length; r++) {
    const row = rows[r] as unknown[];
    if (!row) {
      continue;
    }

    const label = cellStr(row, dateCol).toLowerCase();

    if (/^load temp/.test(label)) {
      orderedTanks.forEach((tank) => {
        const ci = tankCols.get(tank);
        if (ci === undefined) {
          return;
        }
        const n = toNumber(row[ci]);
        if (n !== null) {
          footer.loadTempByTank[tank] = n;
        }
      });
      continue;
    }

    if (/^voy/.test(label)) {
      orderedTanks.forEach((tank) => {
        const ci = tankCols.get(tank);
        if (ci === undefined) {
          return;
        }
        const range = parseVoyRangeCell(row[ci]);
        if (range) {
          footer.voyMinByTank[tank] = range.min;
          footer.voyMaxByTank[tank] = range.max;
        }
      });
      continue;
    }

    if (FOOTER_RE.test(label)) {
      continue;
    }

    const ts = parseDateCell(row[dateCol]);
    if (!ts) {
      continue;
    }

    const cells = colIndexes.map((ci) => row[ci] ?? '');
    const hasTemp = cells
      .slice(0, orderedTanks.length)
      .some((c) => {
        const n = toNumber(c);
        return n !== null && isHeatingTemp(n);
      });

    if (!hasTemp) {
      continue;
    }

    index.push(ts);
    data.push(cells);
  }

  if (!data.length) {
    throw new Error('No daily temperature rows found in Excel');
  }

  const hasFooter =
    Object.keys(footer.loadTempByTank).length > 0 ||
    Object.keys(footer.voyMaxByTank).length > 0;

  const start = Math.max(0, index.length - LAST_N_DAYS);

  return {
    columns,
    index: index.slice(start),
    data: data.slice(start),
    footer: hasFooter ? footer : undefined,
  };
}

export function excelTableToPrintApi(
  table: HeatPlanExcelTable,
  row: { ship_name?: string; ship_imo?: string | number },
): Record<string, unknown> {
  return {
    vessel_name: row.ship_name,
    imo: row.ship_imo,
    response: {
      columns: table.columns,
      index: table.index,
      data: table.data,
    },
    load_temp: table.footer?.loadTempByTank ?? {},
    voy_min: table.footer?.voyMinByTank ?? {},
    voy_max: table.footer?.voyMaxByTank ?? {},
  };
}

/** Parse Daily Temperature Log / Cargo Heating Plan Excel (6–8 tanks). */
export function parseHeatPlanExcelWorkbook(buffer: ArrayBuffer): HeatPlanExcelTable {
  let workbook: XLSX.WorkBook;
  try {
    workbook = XLSX.read(buffer, { type: 'array', cellDates: true });
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    throw new Error(
      msg.toLowerCase().includes('support')
        ? 'Excel format not supported. Save the file as .xlsx and try again.'
        : `Could not open Excel file: ${msg}`,
    );
  }

  if (!workbook.SheetNames?.length) {
    throw new Error('Excel file has no sheets');
  }

  let lastError: Error | null = null;

  for (const sheetName of workbook.SheetNames) {
    const sheet = workbook.Sheets[sheetName];
    if (!sheet) {
      continue;
    }

    const rows = XLSX.utils.sheet_to_json(sheet, {
      header: 1,
      defval: '',
      raw: false,
    }) as unknown[][];

    if (rows.length < 5) {
      continue;
    }

    try {
      return parseSheetRows(rows);
    } catch (e) {
      lastError = e instanceof Error ? e : new Error(String(e));
    }
  }

  throw (
    lastError ??
    new Error('Could not read Excel — DATE row not found on any sheet')
  );
}

export async function parseHeatPlanExcelFile(file: File): Promise<HeatPlanExcelTable> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (!['xlsx', 'xls', 'xlsm', 'csv'].includes(ext)) {
    throw new Error(
      'File type not supported. Use .xlsx, .xls, .xlsm, or .csv Heat Plan export.',
    );
  }
  const buffer = await file.arrayBuffer();
  return parseHeatPlanExcelWorkbook(buffer);
}

export async function blobLooksLikeExcel(blob: Blob): Promise<boolean> {
  if (
    blob.type.includes('spreadsheet') ||
    blob.type.includes('excel') ||
    blob.type.includes('officedocument')
  ) {
    return true;
  }
  const head = new Uint8Array(await blob.slice(0, 4).arrayBuffer());
  return head[0] === 0x50 && head[1] === 0x4b;
}
